import React, { useState, useEffect } from "react";
import FaqSection from "../components/FaqSection";
import { Helmet } from "react-helmet-async";
import styles from "./ZohoFinal.module.css"; // ✅ SAME CSS
import Navigation from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Plus,
  Minus,
  ArrowRight,
  Phone,
  Mail,
  Building2,
  Shield,
  Network,
  PieChart,
  Users2,
  PlugZap,
  Database,
  Workflow,
} from "lucide-react";

/* Assets */
import OracleLogo from "../assests/images/fusion.png";
import Client1 from "../assests/images/caratlane.jpg";
import Client2 from "../assests/images/bigbasket.jpg";
import Client3 from "../assests/images/curefoods.png";

const siteUrl = "https://www.cloudare.in/services-fusion";
const phoneDisplay = "+1 628-282-5683";
const phoneHref = "+16282825683";
const email = "sales@cloudare.in";
const calendly = "https://calendly.com/cloudare";

const faqList = [
  {
    q: "What Oracle Fusion modules do you implement?",
    a: "We implement Oracle Fusion Financials, SCM, HCM, Procurement, Projects, and Advanced Reporting modules customized to your business processes.",
  },
  {
    q: "Do you provide Oracle Integration Cloud (OIC) services?",
    a: "Yes — we design, develop, and support Oracle Integration Cloud (OIC) solutions including REST/SOAP integrations, ERP and HCM adapters, third-party applications, and on-premise systems.",
  },
  {
    q: "Can Oracle Fusion integrate with Salesforce or legacy ERPs?",
    a: "Absolutely. Using OIC and custom APIs, we enable secure and scalable integrations with Salesforce, NetSuite, SAP, legacy ERPs, and custom-built applications.",
  },
  {
    q: "Do you support GST, taxation, and statutory compliance?",
    a: "Yes — we configure GST, TDS, taxation rules, statutory reporting, and compliance workflows aligned with Indian and global regulatory requirements.",
  },
  {
    q: "Do you offer post-go-live support for Oracle Fusion and OIC?",
    a: "We provide SLA-based post-go-live support including incident management, enhancements, performance optimization, and quarterly health checks.",
  },
  {
    q: "Can you handle data migration to Oracle Fusion?",
    a: "Yes — we manage end-to-end data migration using FBDI, ADFdi, and REST APIs with validation, reconciliation, and audit controls.",
  },
  {
    q: "Do you provide Oracle Fusion customizations and extensions?",
    a: "We develop Fusion-compliant extensions using Oracle Visual Builder, Groovy, REST services, and reports while ensuring Oracle upgrade safety.",
  },
  {
    q: "What types of integrations can be built using OIC?",
    a: "OIC supports real-time, scheduled, and event-based integrations including ERP–CRM, HCM–Payroll, banking, GST portals, and third-party SaaS platforms.",
  },
  {
    q: "Do you offer Oracle Fusion and OIC services in Raipur, Chhattisgarh?",
    a: "Yes — we provide Oracle Fusion and Oracle Integration Cloud (OIC) implementation, integration, and support services for businesses in Raipur, Chhattisgarh, and across India.",
  },
  {
    q: "How do you ensure security and performance in OIC integrations?",
    a: "We implement role-based access, OAuth security, encryption, fault handling, monitoring dashboards, and performance tuning best practices.",
  },
  {
    q: "Oracle Fusion implementation partner in India – what services do you offer?",
    a: "We provide end-to-end Oracle Fusion implementation services in India including consulting, configuration, customization, data migration, testing, training, and post-go-live support.",
  },
  {
    q: "Best Oracle Integration Cloud (OIC) consultants for enterprise integrations?",
    a: "Our certified OIC consultants design and implement secure, scalable, and high-performance integrations between Oracle Fusion, third-party SaaS platforms, and on-premise systems.",
  },
  {
    q: "Oracle Fusion ERP implementation cost in India",
    a: "Oracle Fusion ERP implementation cost depends on modules, users, integrations, and customization scope. We provide flexible pricing models tailored to your business size and needs.",
  },
  {
    q: "Why choose Oracle Fusion over legacy ERP systems?",
    a: "Oracle Fusion offers cloud scalability, real-time analytics, continuous upgrades, improved security, and seamless integrations compared to traditional on-premise ERPs.",
  },
  {
    q: "Oracle OIC integration services for GST, banking, and compliance systems",
    a: "We build OIC integrations for GST portals, banking systems, payment gateways, e-invoicing platforms, and statutory compliance solutions.",
  },
  {
    q: "Oracle Fusion support and AMS services in India",
    a: "We offer Application Management Services (AMS) for Oracle Fusion including incident resolution, enhancements, optimization, and release management.",
  },
  {
    q: "Oracle Fusion Financials implementation for Indian businesses",
    a: "Our Oracle Fusion Financials implementations support GST, multi-ledger accounting, statutory reporting, and India-specific compliance requirements.",
  },
  {
    q: "Oracle Fusion SCM and Procurement cloud services",
    a: "We implement Oracle Fusion SCM and Procurement modules including inventory, order management, sourcing, supplier management, and demand planning.",
  },
  {
    q: "Oracle Fusion HCM implementation and payroll integrations",
    a: "We implement Oracle Fusion HCM and integrate payroll, attendance, and third-party HR systems using OIC and REST APIs.",
  },
  {
    q: "Oracle Fusion and OIC services in Raipur, Chhattisgarh",
    a: "We provide Oracle Fusion ERP and Oracle Integration Cloud services in Raipur, Chhattisgarh, supporting local enterprises and pan-India clients.",
  },
  {
    q: "Oracle Fusion cloud migration from Tally, SAP, or legacy ERP",
    a: "We help organizations migrate from Tally, SAP, or legacy ERPs to Oracle Fusion with minimal downtime and validated data migration.",
  },
  {
    q: "Oracle OIC vs custom API integrations – which is better?",
    a: "OIC offers prebuilt adapters, monitoring, fault handling, and scalability, making it more secure and maintainable than custom-built integrations.",
  },
  {
    q: "Certified Oracle Fusion and OIC consultants in India",
    a: "Our team includes certified Oracle Fusion and OIC consultants with deep domain expertise across finance, supply chain, HR, and integrations.",
  },
  {
    q: "Oracle Fusion upgrade, optimization, and performance tuning services",
    a: "We provide Fusion upgrade readiness, quarterly release impact analysis, performance tuning, and continuous optimization services.",
  },
];

export default function OracleFusionServicesPage() {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <main className={styles.main}>
      <Navigation />

      <Helmet>
        <title>
          Oracle Fusion & OIC Partner — Implementation & Integration | Cloudare
        </title>
        <meta
          name="description"
          content="Oracle Fusion ERP & Oracle Integration Cloud (OIC) implementation, integrations, reporting, and managed services by Cloudare."
        />
        <link rel="canonical" href={siteUrl} />
      </Helmet>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.containerGrid}>
          <div className={styles.headContainer}>
            <img
              src={OracleLogo}
              alt="Oracle Partner"
              className={styles.heroLogo}
            />
            <h1 className={styles.heroTitle1}>
              Oracle Fusion & OIC Implementation Experts
            </h1>
            <p className={styles.heroDesc}>
              We help enterprises implement Oracle Fusion ERP and Oracle
              Integration Cloud (OIC) to streamline finance, supply chain, HR,
              analytics, and enterprise integrations across global systems.
            </p>

            <div className={styles.heroButtons}>
              <a href={calendly} className={styles.btnPrimary}>
                Book a Free Consultation{" "}
                <ArrowRight className={styles.iconSm} />
              </a>
              <a href="#faq" className={styles.btnSecondary}>
                View FAQs <ArrowRight className={styles.iconSm} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className={styles.sectionAlt}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            Why Choose Oracle with Cloudare
          </h2>

          <div className={styles.featureGrid}>
            <Feature
              icon={<Building2 className={styles.iconLg} />}
              title="Enterprise-Grade ERP"
              desc="Scalable Oracle Fusion ERP for finance, SCM, HR, and enterprise operations."
            />
            <Feature
              icon={<Shield className={styles.iconLg} />}
              title="Security & Compliance"
              desc="Built-in Oracle security, role-based access, audit trails, and compliance."
            />
            <Feature
              icon={<PlugZap className={styles.iconLg} />}
              title="Seamless Integrations"
              desc="OIC-powered integrations with cloud, SaaS, and on-prem systems."
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className={styles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Oracle Services We Offer</h2>

          <div className={styles.appGrid}>
            <AppCard
              title="Oracle Fusion Financials"
              desc="GL, AP, AR, Assets, Cash Management, Tax & Compliance."
              icon={<PieChart className={styles.iconLg} />}
            />
            <AppCard
              title="Oracle Fusion HCM"
              desc="Core HR, Payroll integrations, Talent & Workforce Management."
              icon={<Users2 className={styles.iconLg} />}
            />
            <AppCard
              title="Oracle Fusion SCM"
              desc="Procurement, Inventory, Order Management & Supply Planning."
              icon={<Database className={styles.iconLg} />}
            />
            <AppCard
              title="Oracle Integration Cloud (OIC)"
              desc="REST, SOAP, File Server, ERP & SaaS integrations."
              icon={<Network className={styles.iconLg} />}
            />
            <AppCard
              title="Reporting & Analytics"
              desc="OTBI, BI Publisher, dashboards, and executive reporting."
              icon={<PieChart className={styles.iconLg} />}
            />
            <AppCard
              title="Automation & Workflows"
              desc="Approval workflows, BPM, and enterprise automations."
              icon={<Workflow className={styles.iconLg} />}
            />
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className={styles.sectionAlt}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Enterprise Success Stories</h2>

          <div className={styles.caseGrid}>
            <CaseStudy
              logo={Client1}
              title="Caratlane — Global Manufacturing ERP Rollout"
              points={[
                "Multi-country Fusion Financials",
                "OIC integrations with legacy ERP",
              ]}
            />

            <CaseStudy
              logo={Client2}
              title="Bigbasket — Real-Time Integrations with OIC"
              points={[
                "Oracle Fusion sync",
                "High-volume transaction processing",
              ]}
            />

            <CaseStudy
              logo={Client3}
              title="Curefoods — Real-Time Integrations with OIC"
              points={[
                "Oracle Fusion sync",
                "High-volume transaction processing",
              ]}
            />
          </div>

          <div className={styles.statGrid}>
            <StatCard
              label="Fusion Implementations"
              value="25+"
              maxProgress={99}
            />
            <StatCard
              label="Integration Accuracy"
              value="99.8%"
              maxProgress={98}
            />
            <StatCard
              label="Avg Go-Live Time"
              value="8-12 weeks"
              maxProgress={99}
            />
          </div>
        </div>
      </section>
      {/* Contact */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.contactCard}>
            <div className={styles.contact1}>
              <h2 className={styles.sectionTitle}>Talk to an Oracle Expert</h2>
              <p className={styles.heroDesc}>
                Book a free enterprise consultation.
              </p>

              <div className={styles.heroButtons}>
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.btnPrimary}
                >
                  Book on Calendly <ArrowRight className={styles.iconSm} />
                </a>
                <a href={`mailto:${email}`} className={styles.btnSecondary}>
                  <Mail className={styles.iconSm} /> {email}
                </a>
              </div>
            </div>

            <div className={styles.contactInfo}>
              <ContactRow
                label="Phone"
                value={phoneDisplay}
                href={`tel:${phoneHref}`}
                icon={<Phone className={styles.iconSm} />}
              />
              {/* <ContactRow
                label="Email"
                value={email}
                href={`mailto:${email}`}
                icon={<Mail className={styles.iconSm} />}
              /> */}
              {/* <div className={styles.locationText}>
                🌍 India • KSA • US • Canada • Global
              </div> */}
            </div>
          </div>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          title="Oracle Fusion Cloud ERP — FAQ"
          faqs={faqList.map((x) => ({ question: x.q, answer: x.a }))}
          emitJsonLd
        />
      </div>

      <Footer />
    </main>
  );
}

/* Reused Sub-components */
const Feature = ({ icon, title, desc }) => (
  <div className={styles.featureCard}>
    <div className={styles.featureHeader}>
      <div className={styles.featureIcon}>{icon}</div>
      <h3 className={styles.featureTitle}>{title}</h3>
    </div>
    <p className={styles.featureDesc}>{desc}</p>
  </div>
);

const AppCard = ({ title, desc, icon }) => (
  <div className={styles.appCard}>
    <div className={styles.appHeader}>
      <div className={styles.appIcon}>{icon}</div>
      <h3 className={styles.appTitle}>{title}</h3>
    </div>
    <p className={styles.appDesc}>{desc}</p>
  </div>
);

function CaseStudy({ logo, alt, title, points }) {
  return (
    <div className={styles.caseCard}>
      <div className={styles.caseImage}>
        <img src={logo} alt={alt} className={styles.caseLogo} />
      </div>
      <div>
        {/* <img src={logo} alt={alt} className={zohoStyles.caseLogo} /> */}
        <h3 className={styles.caseTitle}>{title}</h3>
        <ul className={styles.caseList}>
          {points.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, maxProgress }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let start = 0;
    const interval = setInterval(() => {
      start += 2;
      if (start >= maxProgress) {
        start = maxProgress;
        clearInterval(interval);
      }
      setProgress(start);
    }, 20);
    return () => clearInterval(interval);
  }, [maxProgress]);

  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className={styles.statCard}>
      <svg
        className={styles.circleChart}
        width="100"
        height="100"
        viewBox="0 0 100 100"
      >
        <circle
          className={styles.circleBg}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
        />

        <circle
          className={styles.circleProgress}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />

        {/* ✅ MUST be global class */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="middle"
          className="statValue"
        >
          {value}
        </text>
      </svg>

      {/* ✅ MUST be global class */}
      <div className="statLabel">{label}</div>
    </div>
  );
};

const ContactRow = ({ label, value, href, icon }) => (
  <div className={styles.contactRow}>
    <div className={styles.contactIcon}>{icon}</div>
    <div>
      <div className={styles.contactLabel}>{label}</div>
      <a href={href} className={styles.contactValue}>
        {value}
      </a>
    </div>
  </div>
);
